// components/chat/chat-input/MicButton.tsx
import { useEffect } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { useAudioRecording, RecordingState } from "@/hooks/useAudioRecording";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface MicButtonProps {
  onRecordingComplete?: (blob: Blob) => void;
  disabled?: boolean;
  isTranscribing?: boolean;
}

export const MicButton = ({ onRecordingComplete, disabled, isTranscribing }: MicButtonProps) => {
  const {
    recordingState,
    currentTime,
    audioLevel,
    audioBlob,
    permissionStatus,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecording();

  const isRecording = recordingState === "recording";
  const isRequesting = recordingState === "requesting";

  // Auto-deliver blob when recording stops
  useEffect(() => {
    if (recordingState === "stopped" && audioBlob) {
      onRecordingComplete?.(audioBlob);
      resetRecording();
    }
  }, [recordingState, audioBlob]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleClick = async () => {
    if (disabled) return;

    if (isRecording) {
      stopRecording();
    } else {
      try {
        await startRecording();
      } catch {
        if (permissionStatus === "denied") {
          toast.error("Microphone access denied. Please enable it in your browser settings.");
        }
      }
    }
  };

  return (
    <div className="relative flex items-center">
      {isRecording && (
        <div className="mr-2 flex items-center gap-2">
          {/* Audio level indicator */}
          <div className="flex items-center gap-0.5 h-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-0.5 rounded-full transition-all duration-75",
                  audioLevel > i * 0.2 ? "bg-red-500" : "bg-muted-foreground/30"
                )}
                style={{
                  height: `${Math.max(4, Math.min(16, 4 + audioLevel * 12 + (i % 2) * 2))}px`,
                }}
              />
            ))}
          </div>
          {/* Time indicator */}
          <span className="text-xs text-red-500 font-medium tabular-nums">
            {formatTime(currentTime)}
          </span>
        </div>
      )}
      
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isRequesting}
        className={cn(
          "mb-1 mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md p-2 text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
          isRecording 
            ? "bg-red-500 text-white hover:bg-red-600" 
            : "text-primary/50 opacity-100 hover:bg-muted hover:text-accent-foreground"
        )}
        aria-label={isRecording ? "Stop recording" : "Start recording"}
      >
        {isTranscribing ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary/50" />
        ) : isRequesting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : isRecording ? (
          <Square className="h-4 w-4 fill-current" />
        ) : (
          <Mic className="h-5 w-5" />
        )}
      </button>
    </div>
  );
};
